import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TLabelChat } from '../types/label-chat.type';

export type TGetListLabelChatResponse = ApiResponse<IPagination<TLabelChat>>;

export const getListLabelChat = async (params: FetchParams): Promise<TGetListLabelChatResponse> => {
  const url = '/label-chat';

  return axiosInstance.get(url, { params });
};

/**
 * Use Get Label Chat React-Query Hook
 */
export const GET_LIST_LABEL_CHAT_QUERY_KEY = 'get-list-label-chat';

export const getListLabelChatQueryOptions = <TData = TGetListLabelChatResponse>(
  params: FetchParams,
  select?: (data: TGetListLabelChatResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_LABEL_CHAT_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListLabelChat(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListLabelChatQuery<TData>(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListLabelChatQueryOptions<TData>>;
  select: (data: TGetListLabelChatResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListLabelChatQuery(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListLabelChatQueryOptions<TGetListLabelChatResponse>>;
}): ReturnType<typeof useQuery<TGetListLabelChatResponse>>;

// Implementation
export function useGetListLabelChatQuery<TData = TGetListLabelChatResponse>({
  params,
  queryConfig,
  select,
}: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListLabelChatQueryOptions<TData | TGetListLabelChatResponse>>;
  select?: (data: TGetListLabelChatResponse) => TData;
}) {
  return useQuery({
    ...getListLabelChatQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListLabelChatResponse ? TGetListLabelChatResponse : TData>>;
}
